import React, { ReactElement, useEffect, useState } from 'react';
import { matchPath, RouteProps, Switch } from 'react-router-dom';

import { useBreadcrumbs } from '@/components/common/Breadcrumbs/useBreadcrumbs';
import { Breadcrumb } from '@/components/common/Breadcrumbs/BreadcrumbsContext';
import { generateFlatRouteList } from './generateFlatRouteList';
import { browserHistory } from './browserHistory';
import { useWorkspaceLocationSynchronization } from './useWorkspaceLocationSynchronization';
import { EnvUtils } from '@/utils/env.utils';

export interface RouteParams {
  path: string;
  Component: React.FC<any>;
  children?: RouteParams[];
  exact?: boolean;
  title: string;
  readonly customBreadcrumbItem?: JSX.Element;
}

interface RouteToRenderMetadata {
  routeBreadcrumbs: Breadcrumb[];
}

export interface RouteToRender {
  RouteComponent: ReactElement<RouteProps>;
  metadata: RouteToRenderMetadata;
}

interface IRouterTestProps {
  routes: RouteParams[];
}

export default function RouterTest({ routes }: IRouterTestProps) {
  useWorkspaceLocationSynchronization();

  const [routeElementsToRender, setRouteElementsToRender] =
    useState<RouteToRender[]>([]);

  const { setBreadcrumbs } = useBreadcrumbs();

  function applyBreadcrumbs() {
    const currentUrlPath = new URL(window.location.href).pathname;
    const currentRoute = routeElementsToRender.find((el) => {
      const isMatch = matchPath(currentUrlPath, {
        path: el.RouteComponent.props.path,
        exact: el.RouteComponent.props.exact,
      });


      return isMatch;
    });

    if (currentRoute) {
      setBreadcrumbs(currentRoute.metadata.routeBreadcrumbs);
    }
  }

  useEffect(() => {
    setRouteElementsToRender(generateFlatRouteList(routes));
  }, [routes]);

  useEffect(() => {
    const removeHistoryListener =
      browserHistory.listen(() => {
        setRouteElementsToRender(generateFlatRouteList(routes));
      });

    return () => removeHistoryListener();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    applyBreadcrumbs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeElementsToRender]);

  return (
    <Switch>
      {routeElementsToRender.map((el) => {
        // temporary hide item in production env.
        // Jira Story: https://jira.accessnet.co.uk/browse/ECB-1519
        const hiddenRoute = el.RouteComponent.props.path === '/add-contract';

        const isHiddenInProdItem = hiddenRoute && EnvUtils.azureEnvIs('production');

        const isHiddentUpliftRatesItem = ((el.RouteComponent.props.path?.indexOf('/uplift-rates') ?? -1) !== -1)
          && !EnvUtils.isUpliftRatesEnabled();

        if (!isHiddenInProdItem && !isHiddentUpliftRatesItem) {
          return el.RouteComponent;
        }

        return null;
      })}
    </Switch>
  );
}
